import { MutationCache, type QueryClient } from "@tanstack/react-query"

/**
 * Garantie structurelle anti-UI-périmée. Toute mutation créée via
 * `useApiMutation` (src/hooks/use-api-mutation.ts) pose `meta.entity` sur les
 * racines de clé qu'elle affecte ; cette policy unique invalide, à chaque
 * mutation réussie, toute query dont la clé commence par une de ces racines.
 * Les pages n'ont donc jamais à se souvenir d'invalider à la main les caches
 * dont d'autres pages dépendent.
 *
 * Extraite ici (plutôt qu'inline dans App.tsx) pour qu'App.tsx ET le property
 * test de `useApiMutation` consomment le MÊME code : fin de la reproduction
 * byte-pour-byte de la policy, qui pouvait diverger en silence.
 *
 * `getQueryClient` est un accès paresseux au QueryClient : le `MutationCache`
 * est construit AVANT le `QueryClient` qui le référence
 * (`new QueryClient({ mutationCache: buildMutationCache(() => queryClient) })`),
 * donc on ne peut pas capturer la valeur — seulement une closure invoquée plus
 * tard, à la première mutation réussie, quand le binding est initialisé.
 */
export function buildMutationCache(getQueryClient: () => QueryClient): MutationCache {
  return new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const entities = mutation.meta?.entity
      if (!entities) return
      const queryClient = getQueryClient()
      entities.forEach((entity) => {
        queryClient.invalidateQueries({ queryKey: [entity] })
      })
    },
  })
}
